<template>
	<div>
		<canvas :id='"world-map-" + chartId'></canvas>
	</div>
</template>

<script>
import { Chart } from "chart.js";
import {
	ChoroplethController,
	ColorScale,
	GeoFeature,
	ProjectionScale,
} from "chartjs-chart-geo";
import ChartDataLabels from "chartjs-plugin-datalabels";

Chart.register(ChoroplethController);
Chart.register(GeoFeature);
Chart.register(ColorScale);
Chart.register(ProjectionScale);

import countriesTopoJson from "world-atlas/countries-50m.json";

const countryNamesByCodeAbbrev = {
	AF: "Afghanistan",
	AX: "Åland Islands",
	AL: "Albania",
	DZ: "Algeria",
	AS: "American Samoa",
	AD: "Andorra",
	AO: "Angola",
	AI: "Anguilla",
	AQ: "Antarctica",
	AG: "Antigua and Barbuda",
	AR: "Argentina",
	AM: "Armenia",
	AW: "Aruba",
	AU: "Australia",
	AT: "Austria",
	AZ: "Azerbaijan",
	BS: "Bahamas",
	BH: "Bahrain",
	BD: "Bangladesh",
	BB: "Barbados",
	BY: "Belarus",
	BE: "Belgium",
	BZ: "Belize",
	BJ: "Benin",
	BM: "Bermuda",
	BT: "Bhutan",
	BO: "Bolivia, Plurinational State of",
	BQ: "Bonaire, Sint Eustatius and Saba",
	BA: "Bosnia and Herzegovina",
	BW: "Botswana",
	BV: "Bouvet Island",
	BR: "Brazil",
	IO: "British Indian Ocean Territory",
	BN: "Brunei Darussalam",
	BG: "Bulgaria",
	BF: "Burkina Faso",
	BI: "Burundi",
	CV: "Cabo Verde",
	KH: "Cambodia",
	CM: "Cameroon",
	CA: "Canada",
	KY: "Cayman Islands",
	CF: "Central African Republic",
	TD: "Chad",
	CL: "Chile",
	CN: "China",
	CX: "Christmas Island",
	CC: "Cocos (Keeling) Islands",
	CO: "Colombia",
	KM: "Comoros",
	CG: "Congo",
	CD: "Congo, Democratic Republic of the",
	CK: "Cook Islands",
	CR: "Costa Rica",
	CI: "Côte d'Ivoire",
	HR: "Croatia",
	CU: "Cuba",
	CW: "Curaçao",
	CY: "Cyprus",
	CZ: "Czechia",
	DK: "Denmark",
	DJ: "Djibouti",
	DM: "Dominica",
	DO: "Dominican Republic",
	EC: "Ecuador",
	EG: "Egypt",
	SV: "El Salvador",
	GQ: "Equatorial Guinea",
	ER: "Eritrea",
	EE: "Estonia",
	SZ: "Eswatini",
	ET: "Ethiopia",
	FK: "Falkland Islands (Malvinas)",
	FO: "Faroe Islands",
	FJ: "Fiji",
	FI: "Finland",
	FR: "France",
	GF: "French Guiana",
	PF: "French Polynesia",
	TF: "French Southern Territories",
	GA: "Gabon",
	GM: "Gambia",
	GE: "Georgia",
	DE: "Germany",
	GH: "Ghana",
	GI: "Gibraltar",
	GR: "Greece",
	GL: "Greenland",
	GD: "Grenada",
	GP: "Guadeloupe",
	GU: "Guam",
	GT: "Guatemala",
	GG: "Guernsey",
	GN: "Guinea",
	GW: "Guinea-Bissau",
	GY: "Guyana",
	HT: "Haiti",
	HM: "Heard Island and McDonald Islands",
	VA: "Holy See",
	HN: "Honduras",
	HK: "Hong Kong",
	HU: "Hungary",
	IS: "Iceland",
	IN: "India",
	ID: "Indonesia",
	IR: "Iran, Islamic Republic of",
	IQ: "Iraq",
	IE: "Ireland",
	IM: "Isle of Man",
	IL: "Israel",
	IT: "Italy",
	JM: "Jamaica",
	JP: "Japan",
	JE: "Jersey",
	JO: "Jordan",
	KZ: "Kazakhstan",
	KE: "Kenya",
	KI: "Kiribati",
	KP: "Korea, Democratic People's Republic of",
	KR: "Korea, Republic of",
	KW: "Kuwait",
	KG: "Kyrgyzstan",
	LA: "Lao People's Democratic Republic",
	LV: "Latvia",
	LB: "Lebanon",
	LS: "Lesotho",
	LR: "Liberia",
	LY: "Libya",
	LI: "Liechtenstein",
	LT: "Lithuania",
	LU: "Luxembourg",
	MO: "Macao",
	MG: "Madagascar",
	MW: "Malawi",
	MY: "Malaysia",
	MV: "Maldives",
	ML: "Mali",
	MT: "Malta",
	MH: "Marshall Islands",
	MQ: "Martinique",
	MR: "Mauritania",
	MU: "Mauritius",
	YT: "Mayotte",
	MX: "Mexico",
	FM: "Micronesia, Federated States of",
	MD: "Moldova, Republic of",
	MC: "Monaco",
	MN: "Mongolia",
	ME: "Montenegro",
	MS: "Montserrat",
	MA: "Morocco",
	MZ: "Mozambique",
	MM: "Myanmar",
	NA: "Namibia",
	NR: "Nauru",
	NP: "Nepal",
	NL: "Netherlands",
	NC: "New Caledonia",
	NZ: "New Zealand",
	NI: "Nicaragua",
	NE: "Niger",
	NG: "Nigeria",
	NU: "Niue",
	NF: "Norfolk Island",
	MK: "North Macedonia",
	MP: "Northern Mariana Islands",
	NO: "Norway",
	OM: "Oman",
	PK: "Pakistan",
	PW: "Palau",
	PS: "Palestine, State of",
	PA: "Panama",
	PG: "Papua New Guinea",
	PY: "Paraguay",
	PE: "Peru",
	PH: "Philippines",
	PN: "Pitcairn",
	PL: "Poland",
	PT: "Portugal",
	PR: "Puerto Rico",
	QA: "Qatar",
	RE: "Réunion",
	RO: "Romania",
	RU: "Russia",
	RW: "Rwanda",
	BL: "Saint Barthélemy",
	SH: "Saint Helena, Ascension and Tristan da Cunha",
	KN: "Saint Kitts and Nevis",
	LC: "Saint Lucia",
	MF: "Saint Martin (French part)",
	PM: "Saint Pierre and Miquelon",
	VC: "Saint Vincent and the Grenadines",
	WS: "Samoa",
	SM: "San Marino",
	ST: "Sao Tome and Principe",
	SA: "Saudi Arabia",
	SN: "Senegal",
	RS: "Serbia",
	SC: "Seychelles",
	SL: "Sierra Leone",
	SG: "Singapore",
	SX: "Sint Maarten (Dutch part)",
	SK: "Slovakia",
	SI: "Slovenia",
	SB: "Solomon Islands",
	SO: "Somalia",
	ZA: "South Africa",
	GS: "South Georgia and the South Sandwich Islands",
	SS: "South Sudan",
	ES: "Spain",
	LK: "Sri Lanka",
	SD: "Sudan",
	SR: "Suriname",
	SJ: "Svalbard and Jan Mayen",
	SE: "Sweden",
	CH: "Switzerland",
	SY: "Syrian Arab Republic",
	TW: "Taiwan, Province of China",
	TJ: "Tajikistan",
	TZ: "Tanzania, United Republic of",
	TH: "Thailand",
	TL: "Timor‑Leste",
	TG: "Togo",
	TK: "Tokelau",
	TO: "Tonga",
	TT: "Trinidad and Tobago",
	TN: "Tunisia",
	TR: "Türkiye",
	TM: "Turkmenistan",
	TC: "Turks and Caicos Islands",
	TV: "Tuvalu",
	UG: "Uganda",
	UA: "Ukraine",
	AE: "United Arab Emirates",
	GB: "United Kingdom",
	UM: "United States Minor Outlying Islands",
	US: "United States of America",
	UY: "Uruguay",
	UZ: "Uzbekistan",
	VU: "Vanuatu",
	VE: "Venezuela, Bolivarian Republic of",
	VN: "Viet Nam",
	VG: "Virgin Islands, British",
	VI: "Virgin Islands, U.S.",
	WF: "Wallis and Futuna",
	EH: "Western Sahara",
	YE: "Yemen",
	ZM: "Zambia",
	ZW: "Zimbabwe",
};

export default {
	name: "world-map",
	props: {
		chartId: String,
		srvPhaseId: Number,
		propertyTypeKeyPart: String,
		apValueColumn: String,
	},
	computed: {
		phaseId() {
			return this.srvPhaseId !== undefined
				? this.srvPhaseId
				: pp.gameState.phaseId;
		},
		countries() {
			return topojson.feature(
				countriesTopoJson,
				countriesTopoJson.objects.countries,
			).features;
		},
		presentCountriesInGame() {
			return Object.values(VueStore.getters.gameActors()).filter(
				(actorItem) => {
					// pp.log("check", actorItem, actorItem.functions.includes('country') && actorItem.acronyms[I18n.locale])
					return (
						actorItem.functions.includes("country") &&
						actorItem.acronyms[I18n.locale] !== undefined
					);
				},
			);
		},
		// Map TopoJSON names to the (localized) country names from the DB
		presentCountriesNameMap() {
			return this.presentCountriesInGame.reduce((hash, actorItem) => {
				const countryCode = actorItem.acronyms[I18n.locale].toUpperCase();
				hash[countryNamesByCodeAbbrev[countryCode]] =
					actorItem.names[I18n.locale];
				return hash;
			}, {});
		},
		// Map TopoJSON names to the country id from the DB
		presentCountriesIdMap() {
			return this.presentCountriesInGame.reduce((hash, actorItem) => {
				const countryCode = actorItem.acronyms[I18n.locale].toUpperCase();
				hash[countryNamesByCodeAbbrev[countryCode]] = actorItem.id;
				return hash;
			}, {});
		},
		propertiesPerCountry() {
			const props = this.presentCountriesInGame.reduce((hash, actorItem) => {
				// pp.log("filter for", actorItem.id)
				const apsWithTypeKey = VueStore.getters
					.actorsPropertiesFor(actorItem.id)
					.filter(
						(ap) => ap.property.type_key.indexOf(this.propertyTypeKeyPart) == 0,
					);

				hash[actorItem.id] = apsWithTypeKey; //.filter(ap => ap.values[this.phaseId][this.apValueColumn] == 1)
				// pp.log("- got", actorItem.names[I18n.locale], hash[actorItem.id].map(ap => `${ap.property.names[I18n.locale]}`))
				return hash;
			}, {});
			// pp.log("returning propertiesPerCountry", props);
			return props;
		},
		chartData() {
			return {
				datasets: [
					{
						// labels: this.countries.filter(feature => this.presentCountriesNameMap[feature.properties.name] !== undefined).map(feature => `${this.presentCountriesNameMap[feature.properties.name]} ${this.propertiesPerCountry}`),
						data: this.countries.map((feature) => {
							// pp.log("setting f", feature);
							const topoJSONname = feature.properties.name;
							const actorId = this.presentCountriesIdMap[topoJSONname];
							Object.assign(feature.properties, {
								localizedName: this.presentCountriesNameMap[topoJSONname] || "",
								actorId: actorId,
								colorClass: actorId
									? VueStore.getters.actorData(actorId).color
									: "bg-theme-light",
								propertiesPerCountry: this.propertiesPerCountry[actorId],
							});
							return {
								feature: feature,
								// value: this.presentCountriesNameMap[feature.properties.name] !== undefined ? 1 : 0,
							};
						}),
						backgroundColor: function (context, item) {
							// pp.log("setting backgroundColor", context, item);
							const itemToDraw = context.dataset.data[context.index];
							const colorClass =
								itemToDraw.feature.properties.colorClass || "bg-theme-light";
							// pp.log("- got colorClass", colorClass, countryName)
							return pp.viewHelpers.detectCssRuleFor(
								`.${colorClass}`,
								"background-color",
							);
						},
					},
				],
			};
		},
		chartOptions() {
			return {
				parsing: false, // Required for custom x/y
				showOutline: false,
				showGraticule: true,
				plugins: {
					legend: {
						display: false,
					},
					tooltip: {
						enabled: false,
					},
					datalabels: {
						backgroundColor: function (context) {
							const itemToDraw =
								context.chart.data.datasets[context.datasetIndex].data[
									context.dataIndex
								];
							// pp.log("backk", context, itemToDraw, itemToDraw.feature.properties.name);
							return context.active &&
								itemToDraw.feature.properties.actorId !== undefined
								? "rgba(104, 104, 104, 0.75)"
								: null;
						},
						// align: 'end',
						// anchor: 'end',
						borderRadius: 4,
						color: "white",
						// padding: 8,
						padding: {
							top: 4,
							bottom: 4,
							right: 8,
							left: 8,
						},
						font: {
							// weight: 'bold'
							size: 14,
						},
						formatter: (item, context) => {
							// pp.log("formatter", item, item.feature.properties, context);
							let displayName = item.feature.properties.localizedName;
							if (!displayName.length || !context.active) {
								return "";
							}
							displayName += ":";
							if (
								!item.feature.properties.propertiesPerCountry ||
								!item.feature.properties.propertiesPerCountry.length
							) {
								displayName += "\n-";
							} else {
								item.feature.properties.propertiesPerCountry.forEach((ap) => {
									displayName += `\n${ap.values[this.phaseId][this.apValueColumn] == 1 ? "✔" : "✘"} ${ap.property.names[I18n.locale]}`;
								});
							}
							return displayName;
						},
					},
				},
				scales: {
					xy: {
						projection: "equalEarth",
						projectionScale: 1.5,
						projectionOffset: [0, 60],
					},
					color: {
						display: false,
						legend: {
							display: false,
						},
					},
				},
				onHover: (event, hoverEls) => {
					// pp.log("hov", event, hoverEls);
					if (!hoverEls.length) {
						return;
					}
					// pp.log("at el 21", event, hoverEls[0]);
					const el = hoverEls[0].element.$datalabels[0]._el;
					// const frame = hoverEls[0].element.$datalabels[0]._rects.frame;

					// pp.log("at el 2", el, hoverEls[0].element.$datalabels[0]._rects.frame);
					el.x = event.chart.width / 2;
					el.y = event.chart.height / 2;
				},
			};
		},
	},
	mounted() {
		pp.log("mount", this, this.propertyTypeKeyPart, this.phaseId, this.chartId);

		// @ts-expect-error
		const context = document
			.getElementById(`world-map-${this.chartId}`)
			// @ts-expect-error
			.getContext("2d");
		// @ts-expect-error
		this.chart = new Chart(context, {
			type: "choropleth",
			data: this.chartData,
			// @ts-expect-error
			options: this.chartOptions,
			plugins: [ChartDataLabels],
		});

		pp.vue.bus.$on("CHART-datasets_changed", (data) => {
			if (data.chartId == this.chartId) {
				pp.log("change", this.chartId, data);
				if (data.phaseId) {
					// @ts-expect-error
					this.srvPhaseId = parseInt(data.phaseId, 10);
				}
				if (data.propertyTypeKeyPart) {
					// @ts-expect-error
					this.propertyTypeKeyPart = data.propertyTypeKeyPart;
				}
			}
		});
	},
	watch: {
		// Need to watch the chartData which will be changed when perspectiveActorId or srvPhaseId are changed.
		// Normally this is beign done by the `reactiveProp` mixin from Vue but it doesn't work with the sankey chart
		chartData: function () {
			pp.log("reseting world_map data", this.chart, this.chartData.datasets[0]);
			// @ts-expect-error
			this.chart.data.datasets[0].data = this.chartData.datasets[0].data;
			// this.chart.reset();
			this.chart.update();
		},
	},
	beforeDestroy() {
		pp.vue.bus.$off("CHART-datasets_changed");
	},
};
</script>
